import { createAsyncThunk } from "@reduxjs/toolkit";
import { ThunkApiConfig } from "@shared/models/redux";
import { fetchUserProfile } from "@user/store/thunks/fetchUserProfile";
import { fetchUserTerms } from "@terms/store/thunks/fetchUserTerms";
import { fetchUserSegmentFunction } from "@user/store/thunks/fetchUserSegmentFunction";
import { fetchUserRolesThunk, fetchUserIFCsThunk } from "@myProfile/store/thunks";

/**
 * Initialize the application by fetching essential user data.
 *
 * Order of operations:
 * 1. Fetch user profile (required for subsequent calls)
 * 2. In parallel: fetch terms, segment functions, user roles, and IFCs
 *
 * User roles and IFCs are fetched at app initialization to determine
 * profile menu visibility (My Profile, Manage Contacts) based on
 * the user's role (Primary Contact, Secondary Contact, etc.) and fund assignments.
 */
export const initializeApp = createAsyncThunk<void, void, ThunkApiConfig>(
  "app/initializeApp",
  async (_, { dispatch }) => {
    try {
      // Step 1: Fetch user profile first (needed before other slices read it).
      await dispatch(fetchUserProfile()).unwrap();

      // Step 2: Fetch remaining data in parallel.
      // Roles + IFCs go through contact-service /user/* which derives identity from the JWT —
      // no contactId argument needed here.
      const parallelFetches: Promise<unknown>[] = [
        dispatch(fetchUserTerms()),
        dispatch(fetchUserSegmentFunction()),
        dispatch(fetchUserRolesThunk()),
        dispatch(fetchUserIFCsThunk()),
      ];

      await Promise.all(parallelFetches);
    } catch (error) {
      console.error(error);
    }
  }
);
